import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FileText, Eye, Check, Send,
  AlertCircle, CheckCircle, Clock,
} from 'lucide-react';
import Loading from '../common/Loading';
import NigerianResultSheet from '../common/NigerianResultSheet';

const { REACT_APP_API_URL } = process.env;

const AdminResultManagement = () => {
  const [activeTab, setActiveTab]           = useState('pending');
  const [results, setResults]               = useState([]);
  const [loading, setLoading]               = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [message, setMessage]               = useState({ type: '', text: '' });
  const [selectedTerm, setSelectedTerm]     = useState('First Term');
  const [selectedSession, setSelectedSession] = useState('2024/2025');

  const token = localStorage.getItem('accessToken');

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingResults();
    } else if (activeTab === 'all') {
      fetchAllResults();
    }
  }, [activeTab, selectedTerm, selectedSession]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchPendingResults = async () => {
    try {
      setLoading(true);
      setLoadingPercent(10);
      const res = await axios.get(`${REACT_APP_API_URL}/api/admin/results/submitted`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { term: selectedTerm, session: selectedSession }
      });
      setLoadingPercent(70);
      setResults(res.data.results || []);
      setLoadingPercent(100);
    } catch (err) {
      console.error('Failed to fetch pending results:', err);
      showMessage('error', 'Failed to load pending results.');
      setLoadingPercent(100);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const fetchAllResults = async () => {
    try {
      setLoading(true);
      setLoadingPercent(10);
      const res = await axios.get(`${REACT_APP_API_URL}/api/admin/results`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { term: selectedTerm, session: selectedSession }
      });
      setLoadingPercent(70);
      setResults(res.data.results || []);
      setLoadingPercent(100);
    } catch (err) {
      console.error('Failed to fetch results:', err);
      showMessage('error', 'Failed to load results.');
      setLoadingPercent(100);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  if (loading) {
    return <Loading percentage={loadingPercent} />;
  }

  return (
    <div className="container-fluid py-4" style={{ paddingTop: '80px' }}>
      <div className="card shadow-lg rounded-4 p-3 p-md-4 mb-4 border-0">

        {/* Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 border-bottom pb-3 gap-3">
          <div>
            <h4 className="fw-bold text-primary d-flex align-items-center mb-2 fs-5 fs-md-4">
              <FileText size={24} className="me-2" /> Result Management
            </h4>
            <p className="text-muted mb-0 small">Review and manage student results</p>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible`}>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })} />
          </div>
        )}

        {/* Tabs */}
        <ul className="nav nav-pills mb-4 gap-2 flex-column flex-md-row">
          <li className="nav-item w-100 w-md-auto">
            <button
              className={`nav-link rounded-3 w-100 d-flex align-items-center justify-content-center ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              <Clock size={16} className="me-2" />
              <span className="small">Pending</span>
              {results.filter(r => r.status === 'submitted').length > 0 && activeTab === 'pending' && (
                <span className="badge bg-danger ms-2">
                  {results.filter(r => r.status === 'submitted').length}
                </span>
              )}
            </button>
          </li>
          <li className="nav-item w-100 w-md-auto">
            <button
              className={`nav-link rounded-3 w-100 d-flex align-items-center justify-content-center ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              <CheckCircle size={16} className="me-2" />
              <span className="small">All Results</span>
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        {activeTab === 'pending' && (
          <PendingResultsTab
            results={results}
            loading={loading}
            selectedTerm={selectedTerm}
            setSelectedTerm={setSelectedTerm}
            selectedSession={selectedSession}
            setSelectedSession={setSelectedSession}
            token={token}
            onReviewSuccess={() => {
              showMessage('success', 'Result reviewed successfully!');
              fetchPendingResults();
            }}
          />
        )}

        {activeTab === 'all' && (
          <AllResultsTab
            results={results}
            loading={loading}
            selectedTerm={selectedTerm}
            setSelectedTerm={setSelectedTerm}
            selectedSession={selectedSession}
            setSelectedSession={setSelectedSession}
            token={token}
            onActionSuccess={() => {
              showMessage('success', 'Action completed successfully!');
              fetchAllResults();
            }}
          />
        )}

      </div>
    </div>
  );
};

// ==================== RESULT PREVIEW MODAL ====================
// Renders the actual NigerianResultSheet with live school branding
const ResultPreviewModal = ({ result, onClose, onApprove, onReject, showActions = false }) => {
  const token    = localStorage.getItem('accessToken');
  const printRef = useRef();

  const [reviewing,       setReviewing]       = useState(false);
  const [school,          setSchool]          = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(true);

  // Fetch school branding once when modal opens
  useEffect(() => {
    axios.get(`${REACT_APP_API_URL}/api/admin/school-branding`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(res => setSchool(res.data.school || {}))
    .catch(() => setSchool({}))
    .finally(() => setLoadingBranding(false));
  }, []); // eslint-disable-line

  const handleReview = async (action) => {
    setReviewing(true);
    try {
      if (action === 'approve') await onApprove(result._id);
      else                       await onReject(result._id);
    } finally {
      setReviewing(false);
    }
  };

  // Shape props the way NigerianResultSheet expects them
  const studentProp = {
    name:           result.student?.name           || '',
    admNo:          result.student?.regNo          || '',
    gender:         result.student?.gender         || result.studentExtras?.gender || '',
    dob:            result.student?.dob            || result.studentExtras?.dob    || '',
    className:      result.classId?.name           || '',
    club:           result.student?.club           || result.studentExtras?.club   || '',
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">

          {/* Header */}
          <div className="modal-header py-2">
            <h6 className="modal-title fw-bold mb-0">
              Result Sheet — {result.student?.name}
              <span className={`badge ms-2 bg-${
                result.status === 'draft'     ? 'secondary' :
                result.status === 'submitted' ? 'primary'   :
                result.status === 'approved'  ? 'success'   :
                result.status === 'rejected'  ? 'danger'    : 'info'
              }`}>{result.status}</span>
            </h6>
            <div className="d-flex gap-2 align-items-center">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()}>
                🖨️ Print
              </button>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
          </div>

          {/* Body — the actual result sheet */}
          <div className="modal-body p-2 p-md-3" ref={printRef}>
            {loadingBranding ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
                <p className="text-muted mt-2 small">Loading result sheet…</p>
              </div>
            ) : (
              <NigerianResultSheet
                result={result}
                school={school}
                student={studentProp}
                classSize={result.classSize || 0}
                termBegins={result.termBegins || school?.currentTermBegins || ''}
                termEnds={result.termEnds || school?.currentTermEnds || ''}
                nextTermBegins={result.nextTermResumption || school?.currentNextTermBegins || ''}
              />
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer py-2">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Close
            </button>
            {showActions && result.status === 'submitted' && (
              <>
                <button
                  type="button" className="btn btn-danger btn-sm"
                  onClick={() => handleReview('reject')} disabled={reviewing}
                >
                  {reviewing ? <><span className="spinner-border spinner-border-sm me-1" />Processing…</> : 'Reject'}
                </button>
                <button
                  type="button" className="btn btn-success btn-sm"
                  onClick={() => handleReview('approve')} disabled={reviewing}
                >
                  {reviewing ? <><span className="spinner-border spinner-border-sm me-1" />Processing…</> : 'Approve'}
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

// ==================== PENDING RESULTS TAB ====================
const PendingResultsTab = ({
  results, loading,
  selectedTerm, setSelectedTerm,
  selectedSession, setSelectedSession,
  token, onReviewSuccess
}) => {
  const [selectedResult, setSelectedResult] = useState(null);
  const [approvingAll, setApprovingAll]     = useState(false);
  const [sessionDraft,  setSessionDraft]    = useState(selectedSession);

  const pendingResults = results.filter(r => r.status === 'submitted');

  const approveAll = async () => {
    if (!pendingResults.length) return;
    if (!window.confirm(`Approve all ${pendingResults.length} results?`)) return;
    try {
      setApprovingAll(true);
      for (const result of pendingResults) {
        await axios.put(
          `${REACT_APP_API_URL}/api/admin/results/${result._id}/review`,
          { action: 'approve' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      alert(`Successfully approved ${pendingResults.length} results!`);
      onReviewSuccess();
    } catch (err) {
      alert('Failed to approve all results: ' + (err.response?.data?.message || err.message));
    } finally {
      setApprovingAll(false);
    }
  };

  const handleApprove = async (resultId) => {
    try {
      await axios.put(
        `${REACT_APP_API_URL}/api/admin/results/${resultId}/review`,
        { action: 'approve' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedResult(null);
      onReviewSuccess();
    } catch (err) {
      alert('Failed to approve: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleReject = async (resultId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    try {
      await axios.put(
        `${REACT_APP_API_URL}/api/admin/results/${resultId}/review`,
        { action: 'reject', rejectionReason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedResult(null);
      onReviewSuccess();
    } catch (err) {
      alert('Failed to reject: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <>
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-3">
          <select className="form-select rounded-3" value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)}>
            <option value="First Term">First Term</option>
            <option value="Second Term">Second Term</option>
            <option value="Third Term">Third Term</option>
          </select>
        </div>
        <div className="col-12 col-md-3">
          <input type="text" className="form-control rounded-3" placeholder="Session (e.g. 2024/2025)"
            value={sessionDraft}
            onChange={e => setSessionDraft(e.target.value)}
            onBlur={() => setSelectedSession(sessionDraft)}
            onKeyDown={e => e.key === 'Enter' && setSelectedSession(sessionDraft)} />
        </div>
        <div className="col-12 col-md-6 text-md-end">
          {pendingResults.length > 0 && (
            <button className="btn btn-success rounded-3 w-100 w-md-auto" onClick={approveAll} disabled={approvingAll}>
              {approvingAll
                ? <><span className="spinner-border spinner-border-sm me-2" />Approving…</>
                : <><Check size={18} className="me-2" />Approve All ({pendingResults.length})</>
              }
            </button>
          )}
        </div>
      </div>

      {pendingResults.length === 0 ? (
        <div className="alert alert-success rounded-3">
          <CheckCircle size={20} className="me-2" />
          No pending results to review. All caught up!
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-light">
              <tr>
                <th className="small">Student</th>
                <th className="small d-none d-md-table-cell">Class</th>
                <th className="small d-none d-lg-table-cell">Teacher</th>
                <th className="small d-none d-md-table-cell">Subjects</th>
                <th className="small">Overall</th>
                <th className="small">Grade</th>
                <th className="small d-none d-lg-table-cell">Submitted</th>
                <th className="text-center small">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingResults.map(result => (
                <tr key={result._id}>
                  <td>
                    <div className="fw-semibold small">{result.student.name}</div>
                    <small className="text-muted d-block d-md-none">{result.classId.name}</small>
                  </td>
                  <td className="d-none d-md-table-cell"><span className="badge bg-info">{result.classId.name}</span></td>
                  <td className="d-none d-lg-table-cell small">{result.teacher.name}</td>
                  <td className="d-none d-md-table-cell">{result.subjects?.length || 0}</td>
                  <td className="fw-bold small">{result.overallTotal}</td>
                  <td>
                    <span className={`badge bg-${
                      (result.overallGrade === 'A1') ? 'success' :
                      (result.overallGrade === 'B2' || result.overallGrade === 'B3') ? 'primary' :
                      (result.overallGrade?.startsWith('C')) ? 'info' :
                      (result.overallGrade === 'D7' || result.overallGrade === 'E8') ? 'warning' : 'danger'
                    }`}>
                      {result.overallGrade}
                    </span>
                  </td>
                  <td className="d-none d-lg-table-cell">
                    <small>{result.submittedAt ? new Date(result.submittedAt).toLocaleDateString() : '—'}</small>
                  </td>
                  <td>
                    <div className="d-flex gap-2 justify-content-center">
                      <button className="btn btn-sm btn-primary rounded-3" onClick={() => setSelectedResult(result)}>
                        <Eye size={14} className="d-none d-md-inline me-1" />
                        <span className="small">Review</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedResult && (
        <ResultPreviewModal
          result={selectedResult}
          onClose={() => setSelectedResult(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          showActions={true}
        />
      )}
    </>
  );
};

// ==================== ALL RESULTS TAB ====================
const AllResultsTab = ({
  results, loading,
  selectedTerm, setSelectedTerm,
  selectedSession, setSelectedSession,
  token, onActionSuccess
}) => {
  const [statusFilter,    setStatusFilter]    = useState('all');
  const [selectedResults, setSelectedResults] = useState([]);
  const [previewResult,   setPreviewResult]   = useState(null);
  const [sessionDraft,    setSessionDraft]    = useState(selectedSession);

  const filteredResults = results.filter(r =>
    (statusFilter === 'all' || r.status === statusFilter)
  );
  const approvedResults = filteredResults.filter(r => r.status === 'approved');

  const sendMultipleResults = async () => {
    if (!selectedResults.length) { alert('Please select results to send'); return; }
    if (!window.confirm(`Send ${selectedResults.length} result(s) to parents?`)) return;
    try {
      const res = await axios.post(
        `${REACT_APP_API_URL}/api/admin/results/send-multiple`,
        { resultIds: selectedResults },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message);
      setSelectedResults([]);
      onActionSuccess();
    } catch (err) {
      alert('Failed to send results: ' + (err.response?.data?.message || err.message));
    }
  };

  const gradeBadge = (g) =>
    g === 'A1' ? 'success' : (g === 'B2' || g === 'B3') ? 'primary' :
    (g?.startsWith('C')) ? 'info' : (g === 'D7' || g === 'E8') ? 'warning' : 'danger';

  return (
    <>
      <div className="row g-2 g-md-3 mb-4">
        <div className="col-6 col-md-3 col-lg-2">
          <select className="form-select form-select-sm rounded-3" value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)}>
            <option value="">All Terms</option>
            <option value="First Term">First Term</option>
            <option value="Second Term">Second Term</option>
            <option value="Third Term">Third Term</option>
          </select>
        </div>
        <div className="col-6 col-md-3 col-lg-2">
          <input type="text" className="form-control form-control-sm rounded-3" placeholder="Session"
            value={sessionDraft}
            onChange={e => setSessionDraft(e.target.value)}
            onBlur={() => setSelectedSession(sessionDraft)}
            onKeyDown={e => e.key === 'Enter' && setSelectedSession(sessionDraft)} />
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <select className="form-select form-select-sm rounded-3" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="col-12 col-md-3 col-lg-6 text-md-end">
          {selectedResults.length > 0 && (
            <button className="btn btn-sm btn-success rounded-3 w-100 w-md-auto" onClick={sendMultipleResults}>
              <Send size={16} className="me-2" />
              Send {selectedResults.length} to Parents
            </button>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="d-md-none">
        {filteredResults.length === 0 ? (
          <div className="alert alert-info rounded-3">
            <AlertCircle size={20} className="me-2" />No results found.
          </div>
        ) : (
          <div className="row g-3">
            {filteredResults.map(result => (
              <div key={result._id} className="col-12">
                <div className="card border-0 shadow-sm rounded-3">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-start gap-2 flex-grow-1">
                        {result.status === 'approved' && (
                          <input type="checkbox" className="form-check-input mt-1"
                            checked={selectedResults.includes(result._id)}
                            onChange={e => setSelectedResults(e.target.checked
                              ? [...selectedResults, result._id]
                              : selectedResults.filter(id => id !== result._id)
                            )}
                          />
                        )}
                        <div>
                          <h6 className="mb-1 fw-bold">{result.student.name}</h6>
                          <small className="text-muted">{result.student.regNo}</small>
                        </div>
                      </div>
                      <span className={`badge bg-${
                        result.status === 'draft' ? 'secondary' : result.status === 'submitted' ? 'primary' :
                        result.status === 'approved' ? 'success' : result.status === 'rejected' ? 'danger' : 'info'
                      }`}>{result.status}</span>
                    </div>
                    <div className="row g-2 mb-3">
                      <div className="col-6"><small className="text-muted d-block">Class</small><span className="badge bg-info">{result.classId.name}</span></div>
                      <div className="col-6"><small className="text-muted d-block">Grade</small><span className={`badge bg-${gradeBadge(result.overallGrade)}`}>{result.overallGrade}</span></div>
                      <div className="col-6"><small className="text-muted d-block">Score</small><span className="fw-bold">{result.overallTotal}</span></div>
                      <div className="col-6"><small className="text-muted d-block">Teacher</small><span className="small">{result.teacher.name}</span></div>
                    </div>
                    <button className="btn btn-sm btn-outline-primary rounded-3 w-100" onClick={() => setPreviewResult(result)}>
                      <Eye size={14} className="me-1" />Preview Result
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="d-none d-md-block table-responsive">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }}>
                <input type="checkbox" className="form-check-input"
                  checked={selectedResults.length === approvedResults.length && approvedResults.length > 0}
                  onChange={e => setSelectedResults(e.target.checked ? approvedResults.map(r => r._id) : [])}
                />
              </th>
              <th className="small">Student</th>
              <th className="small">Class</th>
              <th className="small d-none d-lg-table-cell">Term</th>
              <th className="small d-none d-lg-table-cell">Session</th>
              <th className="small">Score</th>
              <th className="small">Grade</th>
              <th className="small">Status</th>
              <th className="small d-none d-lg-table-cell">Teacher</th>
              <th className="small text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.length === 0 ? (
              <tr><td colSpan="10" className="text-center text-muted py-4">No results found.</td></tr>
            ) : filteredResults.map(result => (
              <tr key={result._id}>
                <td>
                  {result.status === 'approved' && (
                    <input type="checkbox" className="form-check-input"
                      checked={selectedResults.includes(result._id)}
                      onChange={e => setSelectedResults(e.target.checked
                        ? [...selectedResults, result._id]
                        : selectedResults.filter(id => id !== result._id)
                      )}
                    />
                  )}
                </td>
                <td>
                  <div className="fw-semibold small">{result.student.name}</div>
                  <small className="text-muted">{result.student.regNo}</small>
                </td>
                <td><span className="badge bg-info">{result.classId.name}</span></td>
                <td className="d-none d-lg-table-cell small">{result.term}</td>
                <td className="d-none d-lg-table-cell small">{result.session}</td>
                <td className="fw-bold small">{result.overallTotal}</td>
                <td><span className={`badge bg-${gradeBadge(result.overallGrade)}`}>{result.overallGrade}</span></td>
                <td>
                  <span className={`badge bg-${
                    result.status === 'draft' ? 'secondary' : result.status === 'submitted' ? 'primary' :
                    result.status === 'approved' ? 'success' : result.status === 'rejected' ? 'danger' : 'info'
                  }`}>{result.status}</span>
                </td>
                <td className="d-none d-lg-table-cell small">{result.teacher.name}</td>
                <td className="text-center">
                  <button className="btn btn-sm btn-outline-primary rounded-3" onClick={() => setPreviewResult(result)}>
                    <Eye size={14} className="me-1" /><span className="small">Preview</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {previewResult && (
        <ResultPreviewModal
          result={previewResult}
          onClose={() => setPreviewResult(null)}
          showActions={false}
        />
      )}
    </>
  );
};

export default AdminResultManagement;